import {
  elevationProfilesAtom,
  elevationProfileToolModeAtom,
  hoveredProfileChainageAtom,
} from '@/atoms';
import { buildSegments, chainageToWorld } from '@/lib/elevationProfile/polylineMath';
import { useFrame, useThree } from '@react-three/fiber';
import { useAtomValue } from 'jotai';
import { useMemo, useRef } from 'react';
import * as THREE from 'three';
import PointX from './PointX';

const SEGMENT_COLOR = 'deepskyblue';
const RIBBON_COLOR = 'deepskyblue';
const RIBBON_OPACITY = 0.15;

const CylinderLine = ({
  start,
  end,
  color,
  opacity = 0.8,
  radius = 0.005,
}: {
  start: THREE.Vector3;
  end: THREE.Vector3;
  color: string;
  opacity?: number;
  radius?: number;
}) => {
  const { midpoint, length, quaternion } = useMemo(() => {
    const mid = start.clone().add(end).multiplyScalar(0.5);
    const len = start.distanceTo(end);
    const dir = new THREE.Vector3().subVectors(end, start).normalize();
    const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
    return { midpoint: mid, length: len, quaternion: quat };
  }, [start, end]);
  if (length < 1e-6) return null;
  return (
    <mesh position={midpoint} quaternion={quaternion} renderOrder={Number.MAX_SAFE_INTEGER - 100}>
      <cylinderGeometry args={[radius, radius, length, 8, 1]} />
      <meshBasicMaterial color={color} transparent opacity={opacity} depthTest={false} />
    </mesh>
  );
};

/**
 * Builds a flat ribbon strip along the polyline at the average Z of each
 * segment, half-width = `buffer`. Two triangles per segment. The ribbon is
 * intentionally flat in XY (rather than following terrain) — it's a
 * footprint indicator for "the area that gets sampled", not a true 3D
 * surface. Rendered at high renderOrder with `depthTest=false` so it stays
 * visible when the camera dips below the polyline.
 *
 * Positions are stored relative to the first point to avoid Float32 precision
 * loss with large UTM-scale coordinates (same trick as AreaMeasuringTool's
 * fillGeometry).
 */
const useRibbonGeometry = (points: ReadonlyArray<[number, number, number]>, buffer: number) => {
  const origin = useMemo(
    () => (points.length > 0 ? new THREE.Vector3(...points[0]) : new THREE.Vector3()),
    [points],
  );
  const geometry = useMemo(() => {
    if (points.length < 2 || buffer <= 0) return null;
    // Each segment contributes a quad → 2 triangles → 6 verts.
    const segCount = points.length - 1;
    const positions = new Float32Array(segCount * 6 * 3);
    let written = 0;
    for (let i = 0; i < segCount; i++) {
      const ax = points[i][0];
      const ay = points[i][1];
      const az = points[i][2];
      const bx = points[i + 1][0];
      const by = points[i + 1][1];
      const bz = points[i + 1][2];
      const dx = bx - ax;
      const dy = by - ay;
      const len = Math.sqrt(dx * dx + dy * dy);
      if (len < 1e-9) continue;
      // Perpendicular in XY plane (left-hand normal of segment direction).
      const nx = -dy / len;
      const ny = dx / len;
      const a1x = ax + nx * buffer - origin.x;
      const a1y = ay + ny * buffer - origin.y;
      const a2x = ax - nx * buffer - origin.x;
      const a2y = ay - ny * buffer - origin.y;
      const b1x = bx + nx * buffer - origin.x;
      const b1y = by + ny * buffer - origin.y;
      const b2x = bx - nx * buffer - origin.x;
      const b2y = by - ny * buffer - origin.y;
      const aZ = az - origin.z;
      const bZ = bz - origin.z;
      // Triangle 1: a1, b1, a2
      positions[written++] = a1x;
      positions[written++] = a1y;
      positions[written++] = aZ;
      positions[written++] = b1x;
      positions[written++] = b1y;
      positions[written++] = bZ;
      positions[written++] = a2x;
      positions[written++] = a2y;
      positions[written++] = aZ;
      // Triangle 2: a2, b1, b2
      positions[written++] = a2x;
      positions[written++] = a2y;
      positions[written++] = aZ;
      positions[written++] = b1x;
      positions[written++] = b1y;
      positions[written++] = bZ;
      positions[written++] = b2x;
      positions[written++] = b2y;
      positions[written++] = bZ;
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(positions.subarray(0, written), 3));
    g.computeVertexNormals();
    return g;
  }, [points, buffer, origin]);
  return { origin, geometry };
};

const calculateMarkerScale = (
  point: THREE.Vector3,
  camera: THREE.Camera,
  renderer: THREE.WebGLRenderer,
) => {
  const d = camera.position.distanceTo(point);
  const pr = renderer.getPixelRatio();
  const height = renderer.domElement.clientHeight * pr;
  if (camera.type === 'OrthographicCamera') {
    const oc = camera as THREE.OrthographicCamera;
    const worldScreenHeight = Math.abs(oc.top - oc.bottom) / oc.zoom;
    return (20 * worldScreenHeight) / height;
  }
  const pc = camera as THREE.PerspectiveCamera;
  const vFOV = (pc.getEffectiveFOV() * Math.PI) / 180;
  const worldScreenHeight = 2 * d * Math.tan(vFOV / 2);
  return (20 * worldScreenHeight) / height;
};

const SavedElevationProfile = ({
  profile,
  isActive,
}: {
  profile: {
    id: string;
    polylinePoints: [number, number, number][];
    bufferDistance: number;
  };
  isActive: boolean;
}) => {
  const camera = useThree((s) => s.camera);
  const renderer = useThree((s) => s.gl);
  const points = useMemo(
    () => profile.polylinePoints.map((p) => new THREE.Vector3(...p)),
    [profile.polylinePoints],
  );
  const { origin, geometry } = useRibbonGeometry(profile.polylinePoints, profile.bufferDistance);

  // Hover marker (only when this profile is the actively hovered one).
  const hovered = useAtomValue(hoveredProfileChainageAtom);
  const segments = useMemo(() => buildSegments(profile.polylinePoints), [profile.polylinePoints]);
  const hoverWorld = useMemo(() => {
    if (!hovered || hovered.profileId !== profile.id) return null;
    const w = chainageToWorld(hovered.chainage, segments);
    return w ? new THREE.Vector3(w.x, w.y, w.z) : null;
  }, [hovered, profile.id, segments]);

  const pointGroupRefs = useRef<(THREE.Group | null)[]>([]);
  const hoverMeshRef = useRef<THREE.Mesh | null>(null);

  useFrame(() => {
    points.forEach((p, i) => {
      const g = pointGroupRefs.current[i];
      if (g) g.scale.setScalar(calculateMarkerScale(p, camera, renderer));
    });
    // Keep the hover sphere at a fixed pixel size on screen by scaling the
    // mesh itself: the underlying geometry has unit radius, so screen
    // radius ≈ markerScale × this multiplier × 20 px (markerScale is
    // calibrated to ~20 px for unit meshes).
    if (hoverMeshRef.current && hoverWorld) {
      hoverMeshRef.current.scale.setScalar(calculateMarkerScale(hoverWorld, camera, renderer) * 0.4);
    }
  });

  return (
    <>
      {points.map((point, i) => (
        <group
          key={i}
          ref={(el) => {
            pointGroupRefs.current[i] = el;
          }}
          position={point.toArray()}
          scale={calculateMarkerScale(point, camera, renderer)}>
          <PointX scale={1} position={new THREE.Vector3()} />
        </group>
      ))}
      {points.slice(0, -1).map((p, i) => (
        <CylinderLine
          key={`seg-${i}`}
          start={p}
          end={points[i + 1]}
          color={SEGMENT_COLOR}
          opacity={isActive ? 0.95 : 0.7}
        />
      ))}
      {geometry && (
        <mesh
          geometry={geometry}
          position={origin}
          renderOrder={Number.MAX_SAFE_INTEGER - 200}>
          <meshBasicMaterial
            color={RIBBON_COLOR}
            transparent
            opacity={RIBBON_OPACITY}
            depthTest={false}
            depthWrite={false}
            side={THREE.DoubleSide}
          />
        </mesh>
      )}
      {hoverWorld && (
        <mesh
          ref={hoverMeshRef}
          position={hoverWorld.toArray()}
          renderOrder={Number.MAX_SAFE_INTEGER}>
          {/* Unit sphere; useFrame scales the mesh to a constant pixel size. */}
          <sphereGeometry args={[1, 16, 16]} />
          <meshBasicMaterial color='red' depthTest={false} />
        </mesh>
      )}
    </>
  );
};

const SavedElevationProfiles = ({ projectId }: { projectId: string }) => {
  const profiles = useAtomValue(elevationProfilesAtom(projectId));
  const mode = useAtomValue(elevationProfileToolModeAtom);
  const enabled = profiles.filter((p) => p.enabled);
  return (
    <>
      {enabled.map((p) => (
        <SavedElevationProfile key={p.id} profile={p} isActive={mode.activeId === p.id} />
      ))}
    </>
  );
};

export default SavedElevationProfiles;
